import { Trophy, Medal, Award, Zap, TrendingUp, Calendar } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getLeaderboard, getUserRank } from '../../lib/gamification/xpEngine';
import { useAuthStore } from '../../store/authStore';

interface LeaderboardEntry {
  id: string;
  user_id: string;
  rank: number;
  score: number;
  profile: {
    full_name: string;
    avatar_url: string | null;
  };
}

type Period = 'weekly' | 'monthly' | 'all_time';
type Metric = 'xp' | 'applications' | 'badges';

export function Leaderboard() {
  const { user } = useAuthStore();
  const [period, setPeriod] = useState<Period>('weekly');
  const [metric, setMetric] = useState<Metric>('xp');
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [userRank, setUserRank] = useState<{ rank: number; score: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, [period, metric, user]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const [leaderboardData, rankData] = await Promise.all([
        getLeaderboard(period, metric, 100),
        user ? getUserRank(user.id, period, metric) : Promise.resolve(null),
      ]);
      setEntries(leaderboardData);
      setUserRank(rankData);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return { icon: Trophy, color: 'text-yellow-500' };
    if (rank === 2) return { icon: Medal, color: 'text-gray-400' };
    if (rank === 3) return { icon: Medal, color: 'text-orange-600' };
    return { icon: Award, color: 'text-gray-300' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Trophy className="w-7 h-7 text-yellow-500" />
          Leaderboard
        </h2>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {/* Period Selector */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setPeriod('weekly')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === 'weekly'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setPeriod('monthly')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === 'monthly'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setPeriod('all_time')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === 'all_time'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All Time
            </button>
          </div>

          {/* Metric Selector */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setMetric('xp')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                metric === 'xp'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              XP
            </button>
            <button
              onClick={() => setMetric('applications')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                metric === 'applications'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Applications
            </button>
            <button
              onClick={() => setMetric('badges')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                metric === 'badges'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Badges
            </button>
          </div>
        </div>
      </div>

      {/* User's Current Rank */}
      {userRank && (
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6" />
              <div>
                <p className="text-sm opacity-90">Your Rank</p>
                <p className="text-2xl font-bold">#{userRank.rank}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-90">{metric === 'xp' ? 'Total XP' : metric === 'applications' ? 'Applications' : 'Badges'}</p>
              <p className="text-2xl font-bold">{userRank.score.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard List */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 px-4 bg-gray-50 rounded-xl">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-medium text-lg">No data yet</p>
          <p className="text-gray-500 mt-1">Be the first to join the leaderboard!</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {entries.map((entry, index) => {
              const { icon: RankIcon, color } = getRankIcon(entry.rank);
              const isCurrentUser = user?.id === entry.user_id;

              return (
                <div
                  key={entry.id}
                  className={`flex items-center gap-4 p-4 transition-colors ${
                    isCurrentUser ? 'bg-purple-50 border-l-4 border-purple-600' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Rank */}
                  <div className="flex items-center justify-center w-12 flex-shrink-0">
                    {entry.rank <= 3 ? (
                      <RankIcon className={`w-8 h-8 ${color}`} />
                    ) : (
                      <span className="text-xl font-bold text-gray-400">#{entry.rank}</span>
                    )}
                  </div>

                  {/* Avatar */}
                  {entry.profile.avatar_url ? (
                    <img
                      src={entry.profile.avatar_url}
                      alt={entry.profile.full_name}
                      className="w-12 h-12 rounded-full border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-lg">
                      {entry.profile.full_name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-gray-900 truncate ${isCurrentUser ? 'text-purple-700' : ''}`}>
                      {entry.profile.full_name}
                      {isCurrentUser && <span className="ml-2 text-sm text-purple-600">(You)</span>}
                    </p>
                  </div>

                  {/* Score */}
                  <div className="flex items-center gap-2 text-lg font-bold text-gray-900">
                    {metric === 'xp' && <Zap className="w-5 h-5 text-yellow-500" />}
                    {metric === 'badges' && <Award className="w-5 h-5 text-purple-500" />}
                    <span>{entry.score.toLocaleString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
